export type QuadroEnum = 'COMERCIAL' | 'CONTRATO_PRIME' | 'TRAMITACAO_VIVO' | 'TRAMITACAO_PRIME' | 'PRE_ATIVO' | 'QUALIDADE' | 'PEDIDO_CANCELADO';

export const QUADRO_LABELS: Record<QuadroEnum, string> = {
  COMERCIAL: 'COMERCIAL',
  CONTRATO_PRIME: 'SUPORTE COMERCIAL',
  TRAMITACAO_VIVO: 'TRAMITAÇÃO',
  TRAMITACAO_PRIME: 'CRÉDITO',
  PRE_ATIVO: 'PRÉ-ATIVO',
  QUALIDADE: 'QUALIDADE',
  PEDIDO_CANCELADO: 'PEDIDO CANCELADO',
};

export function getQuadroLabel(quadro: QuadroEnum | string): string {
  return QUADRO_LABELS[quadro as QuadroEnum] || quadro;
}

// Status que só podem ser atribuídos pela triagem automatizada
export const STATUS_BLOQUEADOS_TRIAGEM = [
  'AGUARDANDO NOTA FISCAL',
  'SMS ENVIADO',
  'CPF / CNPJ DIVERGENTE',
  'PORTABILIDADE AGENDADA',
  '(PN) CONCLUIDO',
  'FINALIZADO PEDIDO (100%)',
];

// Quadros inteiros bloqueados para seleção manual
export const QUADROS_BLOQUEADOS_SELECAO = ['QUALIDADE'];

/**
 * Rótulo de TELA de uma coluna — só o que o usuário lê. A chave continua sendo
 * o nome real da coluna, em todo lugar.
 *
 * ⛔ NÃO renomear a coluna no banco para conseguir isto. O nome é chave literal
 * em `TRIAGEM_POR_COLUNA`, nas queries do Kanban, no `historico_status` de todo
 * pedido que já passou ali e — o mais grave — nos
 * `item_cancelamentos.snapshot_anterior`, que é o que faz a RETOMADA achar de
 * onde o pedido foi cancelado. Renomear quebraria a recuperação dos pedidos
 * antigos em silêncio (57 cancelamentos só de AGUARDANDO NOTA FISCAL).
 *
 * ⛔ E também NÃO usar o `status_catalogo.nome` como rótulo: ele é comparado por
 * texto em [[STATUS_BLOQUEADOS_TRIAGEM]] — mudar lá destravaria a troca manual
 * de status na coluna, furando a LEI de "tudo pela triagem".
 *
 * AGUARDANDO NOTA FISCAL (Thuane, 05/08/2026): a fila trata DUAS coisas — nota
 * fiscal para entrega de aparelho E bilhete de portabilidade. O nome só contava
 * metade da história.
 */
const ROTULO_COLUNA: Record<string, string> = {
  'AGUARDANDO NOTA FISCAL': 'AGUARDANDO NOTA FISCAL / BILHETE',
};

/** Nome que aparece na tela para uma coluna. Sem entrada no mapa = o próprio nome. */
export function rotuloColuna(coluna: string): string {
  return ROTULO_COLUNA[normalizarColuna(coluna)] ?? coluna;
}

// Helper: verifica se um status é bloqueado para seleção manual
export function isStatusBloqueadoTriagem(statusNome: string, quadro: string): boolean {
  if (QUADROS_BLOQUEADOS_SELECAO.includes(quadro)) return true;
  if (quadro === 'PRE_ATIVO' && STATUS_BLOQUEADOS_TRIAGEM.includes(statusNome)) return true;
  return false;
}

/**
 * Coluna → nome da triagem que a trabalha. É a LISTA ÚNICA: quem estiver aqui
 * só se move pela própria triagem, para todo mundo (inclusive master) e por
 * qualquer caminho — selo de status, arrastar card, botão de ação do BKO.
 * Movimento fora da triagem não entra no relatório do BKO.
 *
 * COMERCIAL fica de fora de propósito: não tem triagem, o funil é do consultor.
 * QUALIDADE e PEDIDO_CANCELADO já têm trava própria
 * ([[isStatusBloqueadoTriagem]] e o selo de cancelado) e a recuperação passa
 * por outro caminho — não entram aqui.
 */
export const TRIAGEM_POR_COLUNA: Record<string, Record<string, string>> = {
  CONTRATO_PRIME: {
    'PEDIDO FECHADO': 'Triagem Pedido Fechado',
    'PEDIDO RECUPERADO': 'Triagem Pedido Recuperado',
    'CRIAÇÃO PEDIDO': 'Triagem Criação Pedido',
    'BIOMETRIA': 'Triagem Biometria',
    'ACEITE': 'Triagem Aceite',
    'INPUT': 'Triagem Input',
    'PENDENCIA INPUT': 'Triagem Pendência Input',
    'AGUARDANDO HP': 'Triagem Aguardando HP',
  },
  TRAMITACAO_VIVO: {
    'INPUT REALIZADO MOVEL': 'Triagem Solar - Móvel',
    'INPUT REALIZADO FIXA': 'Triagem Solar - Fixa',
    'EM INSTALAÇÃO': 'Triagem Em instalação',
    'VALIDAÇÃO PENDENTE': 'Triagem Cotação',
    'ERRO SOLAR INPUT MANUAL': 'Triagem Erro Solar',
    '(TT) RECURSOS INVALIDOS': 'Triagem TT Recursos',
    'NEGOCIAÇÃO (ERRO DE INPUT)': 'Triagem Erro de Input',
  },
  TRAMITACAO_PRIME: {
    'ANALISE MANUAL CREDITO': 'Triagem Análise Manual Crédito',
    'CREDITO REPROVADO': 'Triagem Crédito Reprovado',
  },
  PRE_ATIVO: {
    'INSPEÇÃO CONCLUIDA': 'Triagem Ativação',
    'AGUARDANDO NOTA FISCAL': 'Triagem Nota Fiscal',
    'SMS ENVIADO': 'Triagem Portabilidade',
    'PORTABILIDADE AGENDADA': 'Triagem Portabilidade Concluída',
    'CPF / CNPJ DIVERGENTE': 'Triagem CPF / CNPJ Divergente',
    'PEDIDO COMPLEMENTAR': 'Triagem Pedido Complementar',
  },
};

// 'ERRO SOLAR  INPUT MANUAL' tem espaço duplo no banco — sempre normalizar
export function normalizarColuna(coluna: string): string {
  return (coluna || '').toUpperCase().replace(/\s+/g, ' ').trim();
}

export function chavePosVenda(coluna: string): string {
  const chave = normalizarColuna(coluna);
  if (chave === 'VALIDAÇÃO ESIM') return 'ATIVAÇÃO DE CHIP';
  if (chave === 'BOAS VINDAS') return 'BOAS-VINDAS';
  return chave;
}

export function colunaPosVendaNoCatalogo(coluna: string, catalogo: Array<{ coluna: string }>): string {
  return catalogo.find(status => chavePosVenda(status.coluna) === chavePosVenda(coluna))?.coluna ?? coluna;
}

/** Nome da triagem dona da coluna, ou null se a coluna se move na mão. */
export function getTriagemDaColuna(quadro: string, coluna: string): string | null {
  return TRIAGEM_POR_COLUNA[quadro]?.[normalizarColuna(coluna)] ?? null;
}

/** Coluna sem triagem = status ainda pode ser trocado na mão. */
export function podeMudarStatusManual(quadro: string, coluna: string): boolean {
  return !getTriagemDaColuna(quadro, coluna);
}

export function getColunasForQuadro(quadro: QuadroEnum | string): string[] {
  // Colunas são buscadas dinamicamente do banco de dados
  // Esta função é mantida por compatibilidade mas retorna array vazio
  // Use useStatusCatalogo(quadro) para buscar as colunas reais
  return [];
}
